import {Injectable} from '@angular/core';

import {I18NextPipe} from 'angular-i18next';
import {gql} from 'apollo-angular';
import {cloneDeep} from 'app/core/helpers/object';
import {Country} from 'app/core/interfaces/country.interface';
import {CheckboxTreeNode, CheckboxTreePreset} from 'app/shared/components/checkbox-tree/checkbox-tree.interface';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import {of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {listCountries as listCountriesI} from './gql/listCountries';
import {RouterHelperService} from './router-helper.service';

export type CountryFilterFn = (item: Country) => boolean;

const listCountriesQuery = gql`
    query listCountries {
        countries {
            nodes {
                id
                name
                tier
                code
                continentCode
                continentName
                platforms
            }
        }
    }
`;

export class CountrySlice {
    constructor(
        private listCountries: Country[],
        private transform = (key: string, _): string => key,
        public readonly holdAllAvailableCountries: boolean = true
    ) {
        this.listCountries.forEach(country => (country.platforms = new Set(country.platforms)));
    }

    getAll(): Country[] {
        return this.listCountries;
    }

    getByIds(ids: string[]): Country[] {
        return this.listCountries.filter(item => ids.includes(item.id));
    }

    getByCode(codes: string[]): Country[] {
        return this.listCountries.filter(item => codes.includes(item.code));
    }

    getCountriesIds(): string[] {
        return this.listCountries.map(country => country.id);
    }

    getSafeCountriesIds(ids?: Iterable<string>): Array<string> {
        if (!ids) {
            return this.getCountriesIds();
        }
        let all = new Set(this.getCountriesIds());
        return [...ids].filter(id => all.has(id));
    }

    getTiersPresents(): CheckboxTreePreset[] {
        return [
            ...this.listCountries
                .reduce((tiers: Map<any, CheckboxTreePreset>, item) => {
                    if (item.tier) {
                        if (!tiers.has(item.tier)) {
                            tiers.set(item.tier, <CheckboxTreePreset>{
                                label: this.transform('countries.tier', {tier: item.tier}),
                                values: new Set<string>(),
                            });
                        }
                        tiers.get(item.tier).values.add(item.id);
                    }
                    return tiers;
                }, new Map())
                .values(),
        ].sort((a, b) => (a.label > b.label ? 1 : -1));
    }

    getSelectGroupedOptions(): CheckboxTreeNode[] {
        return [
            ...this.listCountries
                .reduce((acc: Map<any, CheckboxTreeNode>, item) => {
                    if (!acc.has(item.continentCode)) {
                        acc.set(item.continentCode, <CheckboxTreeNode>{
                            label: item.continentName,
                            children: [],
                        });
                    }
                    acc.get(item.continentCode).children.push({
                        label: item.name,
                        value: item.id,
                    });
                    return acc;
                }, new Map())
                .values(),
        ];
    }

    prepareFormValues(countries: Iterable<string> = null): Set<string> {
        return new Set(this.getSafeCountriesIds(countries));
    }

    /**
     * return if all countries are selected - return null
     * this values are equal to us
     *
     * @param countries
     */
    prepareFormValuesToSend(countries: Iterable<string>) {
        if (!countries) {
            return [];
        }
        const arrayValue = [...countries];
        if (!arrayValue.length) {
            return [];
        }
        if (arrayValue.length === this.getAll().length) {
            return null;
        }
        return this.getSafeCountriesIds(countries);
    }
}

@Injectable()
export class CountryService {
    // with all countries
    protected slice: CountrySlice;

    protected transform = (key: string, options?: any): string => {
        try {
            return this.i18next.transform(key, options);
        } catch (e) {
            console.error('transformError CountryService', e);
            return key;
        }
    };

    constructor(
        private api: ApiService,
        private i18next: I18NextPipe,
        private routerHelper: RouterHelperService
    ) {}

    resolve() {
        return (
            this.load()
                // just to catch error, user will be correctly redirected to /500 page
                .pipe(catchError(() => of(true)))
                .toPromise()
                .then(() => true)
        );
    }

    load() {
        return this.api
            .query<listCountriesI>({
                query: listCountriesQuery,
                fetchPolicy: 'cache-first',
            })
            .pipe(
                map(r => r.countries.nodes as unknown as Country[]),
                // in case countries has not been loaded, user can not see page correctly, so redirect user to error page
                tap(
                    listCountries => (this.slice = new CountrySlice(cloneDeep(listCountries), this.transform)),
                    error => {
                        this.slice = new CountrySlice([], this.transform);
                        console.debug('[CountryService] failed to fetch countries', error);
                        this.routerHelper.handleNetworkError(error);
                    }
                )
            );
    }

    getSlice(filter: CountryFilterFn = () => true) {
        const filteredList = this.slice.getAll().filter(filter);
        return new CountrySlice(filteredList, this.transform, filteredList.length === this.slice.getAll().length);
    }

    getAll(): Country[] {
        return this.slice ? this.slice.getAll() : [];
    }

    getByIds(ids: string[]): Country[] {
        return this.slice.getByIds(ids);
    }

    getByCode(codes: string[]): Country[] {
        return this.slice.getByCode(codes);
    }

    getCountriesIds(): string[] {
        return this.slice.getCountriesIds();
    }

    getSafeCountriesIds(ids?: Iterable<string>): Array<string> {
        return this.slice.getSafeCountriesIds(ids);
    }

    getTiersPresents(): CheckboxTreePreset[] {
        return this.slice.getTiersPresents();
    }

    getSelectGroupedOptions(): CheckboxTreeNode[] {
        return this.slice.getSelectGroupedOptions();
    }

    prepareFormValuesToSend(countries: Iterable<string>) {
        return this.slice.prepareFormValuesToSend(countries);
    }

    prepareFormValues(countries: Iterable<string> | undefined | null): Set<string> {
        return this.slice.prepareFormValues(countries);
    }
}
